/**
 * usePcsBuddyAssist — page-aware PCS Buddy floating prompt logic.
 *
 * Privacy contract: only safe public-page context is exposed.
 * No tokens, emails, form data, owner drafts, or payment details.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// ── Rail routes (desktop tab layout instead of bottom-left card) ─────────────

const RAIL_PATTERNS = new Set(['/listings', '/locations', '/locations/:id', '/location-match']);

// ── Route skip rules ─────────────────────────────────────────────────────────

const SKIP_EXACT = new Set([
  '/concierge',
  '/settings',
  '/favorites',
  '/notifications',
  '/bookings',
  '/compare',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
]);

const SKIP_PREFIXES = ['/admin', '/owner'];

// ── Prompt configs per route pattern ─────────────────────────────────────────

const ROUTE_PROMPTS = {
  '/': {
    pageType: 'home',
    headline: 'Planning a European PCS?',
    body: 'PCS Buddy can walk you through location options, TLA basics, and what to expect on the ground.',
    cta: 'Learn more',
    expandedGuidance:
      'I can walk you through location options, TLA basics, commute times from key bases, and what to expect your first week in Germany.',
    suggestedQuestions: [
      'Which German city is best for families near Ramstein?',
      'How does TLA work for a European PCS?',
      'What should I know before my first week in Germany?',
    ],
    prefillMessage: "I'm planning a PCS to Europe and have some questions about locations and housing.",
    intentHint: 'pcs_planning',
    delayMs: 70000,
  },
  '/listings': {
    pageType: 'listings',
    headline: 'Need help finding the right home?',
    body: 'PCS Buddy can explain TLA coverage, compare housing types, and help you know what to ask before you inquire.',
    cta: 'See tips',
    expandedGuidance:
      'Before you inquire on a property, it helps to know what TLA covers, which fees are standard, and what questions to ask the owner.',
    suggestedQuestions: [
      'What should I check before I inquire on a property?',
      'How do I compare TLA-ready homes?',
      'What fees or utilities should I confirm upfront?',
    ],
    prefillMessage: "I'm searching for housing near my duty station. Can you help me understand my options?",
    intentHint: 'housing_search',
    delayMs: 60000,
  },
  '/locations': {
    pageType: 'locations_list',
    headline: 'Not sure which location fits your move?',
    body: 'PCS Buddy can compare neighborhoods, base proximity, and family life across Germany.',
    cta: 'Compare locations',
    expandedGuidance:
      "I can compare neighborhoods, base commute times, school options, and family life across Germany's main duty station areas.",
    suggestedQuestions: [
      'How do Wiesbaden and Kaiserslautern compare for families?',
      'Which area is best for a short commute to Grafenwoehr?',
      'What should I ask before choosing between two locations?',
    ],
    prefillMessage: 'Can you help me compare different German locations for a military PCS move?',
    intentHint: 'compare_locations',
    delayMs: 45000,
  },
  '/locations/:id': {
    pageType: 'location_detail',
    headline: 'Exploring this area?',
    body: 'PCS Buddy can compare neighborhoods, base commute times, and what military families look for here.',
    cta: 'Get quick tips',
    expandedGuidance:
      'I can help you compare commute time, family fit, grocery access, schools, and nearby-base convenience for this area.',
    suggestedQuestions: [
      'What should I know before choosing this area?',
      'How does this area compare for families?',
      'What should I ask before booking housing here?',
    ],
    prefillMessage: "I'm looking at housing in this area. Can you tell me more about living here for a military family?",
    intentHint: 'location_detail',
    delayMs: 45000,
  },
  '/location-match': {
    pageType: 'location_match',
    headline: 'Want personalized location advice?',
    body: 'PCS Buddy can help you choose between base proximity and city living based on your family priorities.',
    cta: 'Get advice',
    expandedGuidance:
      'Choosing between base proximity and city living depends on your commute tolerance, family needs, and off-duty priorities. I can help you think it through.',
    suggestedQuestions: [
      'Should I prioritize base proximity or city living?',
      'What matters most for family fit in Germany?',
      'How do I choose between these areas?',
    ],
    prefillMessage: "I'm trying to decide between living near base versus in a nearby city. Can you help?",
    intentHint: 'location_decision',
    delayMs: 30000,
  },
  '/pcs-checklist': {
    pageType: 'pcs_checklist',
    headline: 'Have PCS questions?',
    body: 'PCS Buddy can walk you through relocation priorities specific to your duty station.',
    cta: 'Ask a question',
    expandedGuidance:
      'I can help you prioritize your PCS checklist, flag items specific to Germany, and explain what to expect at each stage of the move.',
    suggestedQuestions: [
      'What should I do first when I get PCS orders to Germany?',
      'What housing paperwork do I need before I arrive?',
      'How early should I start looking for TLA housing?',
    ],
    prefillMessage: "I'm working through my PCS checklist for a move to Europe. I have some questions.",
    intentHint: 'pcs_checklist',
    delayMs: 90000,
  },
  '/tla-calculator': {
    pageType: 'tla_calculator',
    headline: 'Questions about your TLA?',
    body: 'PCS Buddy can explain TLA rates, eligibility, and how to make the most of your allowance.',
    cta: 'Learn about TLA',
    expandedGuidance:
      'I can explain TLA eligibility, how rates are set, what receipts you typically need, and how to budget around your allowance.',
    suggestedQuestions: [
      'How is my TLA rate calculated?',
      'What is covered by TLA in Germany?',
      'How long can I use TLA while I find permanent housing?',
    ],
    prefillMessage: 'Can you explain how TLA works and what I should expect for my move to Germany?',
    intentHint: 'tla_questions',
    delayMs: 45000,
  },
  '/local-favorites': {
    pageType: 'local_favorites',
    headline: 'Want local recommendations?',
    body: 'PCS Buddy knows the best spots near each base — restaurants, markets, and family activities.',
    cta: 'Get recommendations',
    expandedGuidance:
      'I can recommend local restaurants, grocery options, weekend destinations, and family-friendly spots near any major U.S. base in Germany.',
    suggestedQuestions: [
      'What are the best grocery stores for American products near Ramstein?',
      'Good weekend trips for families from Wiesbaden?',
      'Where do military families typically shop near Stuttgart?',
    ],
    prefillMessage: 'What are some good local spots near the major US bases in Germany?',
    intentHint: 'local_tips',
    delayMs: 60000,
  },
};

// ── i18n key prefixes per route ───────────────────────────────────────────────

const ROUTE_KEY_PREFIX = {
  '/': 'pcs_buddy.home',
  '/listings': 'pcs_buddy.listings',
  '/locations': 'pcs_buddy.locations',
  '/locations/:id': 'pcs_buddy.location_detail',
  '/location-match': 'pcs_buddy.location_match',
  '/pcs-checklist': 'pcs_buddy.pcs_checklist',
  '/tla-calculator': 'pcs_buddy.tla_calculator',
  '/local-favorites': 'pcs_buddy.local_favorites',
};

function translatePromptConfig(config, routePattern, t) {
  if (!config || !t) return config;
  const pfx = ROUTE_KEY_PREFIX[routePattern] ?? 'pcs_buddy.home';
  return {
    ...config,
    headline: t(`${pfx}.headline`, config.headline),
    body: t(`${pfx}.body`, config.body),
    cta: t(`${pfx}.cta`, config.cta),
    expandedGuidance: config.expandedGuidance
      ? t(`${pfx}.expanded_guidance`, config.expandedGuidance)
      : config.expandedGuidance,
    prefillMessage: config.prefillMessage
      ? t(`${pfx}.prefill`, config.prefillMessage)
      : config.prefillMessage,
    suggestedQuestions: config.suggestedQuestions?.map((q, i) =>
      t(`${pfx}.q${i + 1}`, q)
    ),
  };
}
const KEY_ROUTE_DISMISS = 'pcs_buddy_dismissed_routes';
const GLOBAL_COOLDOWN_MS = 6 * 60 * 60 * 1000;
const ROUTE_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function isDismissedGlobally() {
  try {
    const ts = localStorage.getItem(KEY_GLOBAL_DISMISS);
    return ts ? Date.now() - parseInt(ts, 10) < GLOBAL_COOLDOWN_MS : false;
  } catch {
    return false;
  }
}

function isDismissedForPattern(pattern) {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY_ROUTE_DISMISS) || '{}');
    const ts = stored[pattern];
    return ts ? Date.now() - ts < ROUTE_COOLDOWN_MS : false;
  } catch {
    return false;
  }
}

function recordDismiss(pattern) {
  try {
    localStorage.setItem(KEY_GLOBAL_DISMISS, String(Date.now()));
    const stored = JSON.parse(localStorage.getItem(KEY_ROUTE_DISMISS) || '{}');
    stored[pattern] = Date.now();
    localStorage.setItem(KEY_ROUTE_DISMISS, JSON.stringify(stored));
  } catch {}
}

function resolveRoutePattern(pathname) {
  if (pathname === '/') return '/';
  if (pathname === '/listings') return '/listings';
  if (pathname === '/locations') return '/locations';
  if (pathname.startsWith('/locations/') && pathname.length > '/locations/'.length) return '/locations/:id';
  if (pathname === '/location-match') return '/location-match';
  if (pathname === '/pcs-checklist') return '/pcs-checklist';
  if (pathname === '/tla-calculator') return '/tla-calculator';
  if (pathname === '/local-favorites') return '/local-favorites';
  return null;
}

function shouldSkipRoute(pathname) {
  if (SKIP_EXACT.has(pathname)) return true;
  return SKIP_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function usePcsBuddyAssist() {
  const location = useLocation();
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const pathname = location.pathname;
  const routePattern = resolveRoutePattern(pathname);
  const rawPromptConfig = routePattern ? ROUTE_PROMPTS[routePattern] : null;
  const promptConfig = translatePromptConfig(rawPromptConfig, routePattern, t);

  useEffect(() => {
    setVisible(false);
    setExpanded(false);

    if (shouldSkipRoute(pathname)) return;
    if (!promptConfig) return;
    if (isDismissedGlobally()) return;
    if (routePattern && isDismissedForPattern(routePattern)) return;

    const timer = setTimeout(() => setVisible(true), promptConfig.delayMs);
    return () => clearTimeout(timer);
  }, [pathname]);

  const expand = useCallback(() => setExpanded(true), []);
  const collapse = useCallback(() => setExpanded(false), []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setExpanded(false);
    if (routePattern) recordDismiss(routePattern);
  }, [routePattern]);

  const openConcierge = useCallback(
    (navigate, questionOverride) => {
      const message = questionOverride ?? promptConfig?.prefillMessage;
      if (message) {
        try {
          localStorage.setItem(
            'pcs_buddy_prefill',
            JSON.stringify({
              message,
              autoSubmit: Boolean(questionOverride),
              pageContext: {
                pageType: promptConfig?.pageType,
                pathname,
                intentHint: promptConfig?.intentHint,
              },
            })
          );
        } catch {}
      }
      dismiss();
      navigate('/concierge');
    },
    [promptConfig, pathname, dismiss]
  );

  const isRailRoute = routePattern ? RAIL_PATTERNS.has(routePattern) : false;

  return { visible, promptConfig, dismiss, openConcierge, expanded, expand, collapse, isRailRoute };
}
